import { Card, CardContent } from '@xhubio-saas/core-frontend/ui-components'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChangelogMarkdown, parseReleases, useChangelogSource } from './settings/changelogMarkdown'
import { ChangelogReleaseNav, ChangelogReleaseSelect } from './settings/changelogReleaseNav'

/**
 * Changelog page — the full release changelog as its own sidebar destination
 * (bottom of the "Einstellungen" group). Relocated here from a collapsed card
 * on the Settings → System page, where the long document fought the version
 * info for space.
 *
 * Layout: a viewport-height two-pane split where the DOCUMENT scrolls in its own
 * pane and the release-index rail sits in a static column — so the list is
 * always visible while reading (position: sticky is unreliable here because the
 * host html/body are themselves overflow containers). On mobile the rail is
 * replaced by an always-visible jump-to-release select in the header. Content is
 * localized (German languages get CHANGELOG.md, everything else CHANGELOG.en.md).
 */
export function ChangelogPage() {
  const { t, i18n } = useTranslation()
  const changelogSource = useChangelogSource()
  const releases = useMemo(() => parseReleases(changelogSource), [changelogSource])
  // Callback ref → state so the rail's scroll-spy can observe within the
  // content pane once it has mounted.
  const [contentEl, setContentEl] = useState<HTMLDivElement | null>(null)

  return (
    <div
      className="container mx-auto flex h-[calc(100dvh-4rem)] flex-col px-4 py-6"
      data-testid="changelog-page"
    >
      <h1 className="mb-4 shrink-0 text-3xl tracking-[-0.04em]">
        {t('settings.system.changelogTitle', 'Changelog')}
      </h1>

      {/* Mobile: the release navigation lives in the always-visible header. */}
      <ChangelogReleaseSelect
        releases={releases}
        locale={i18n.language}
        className="mb-4 shrink-0 lg:hidden"
      />

      {/* Two independently scrolling panes: the rail column never scrolls away
          (always visible); the document scrolls inside its own pane. */}
      <div className="flex min-h-0 flex-1 lg:gap-8">
        <ChangelogReleaseNav
          releases={releases}
          locale={i18n.language}
          scrollRoot={contentEl}
          className="hidden w-60 shrink-0 overflow-y-auto pb-6 lg:block"
        />
        <div
          ref={setContentEl}
          className="min-w-0 flex-1 overflow-y-auto"
          data-testid="changelog-scroll"
        >
          {/* max-w-3xl: reading measure for a long prose document — full-width
              lines are unreadable on wide screens. */}
          <Card className="w-full max-w-3xl" data-testid="changelog-content">
            <CardContent>
              <ChangelogMarkdown source={changelogSource} locale={i18n.language} />
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
